/* eslint-disable @typescript-eslint/no-explicit-any */
import { Signal, batch, computed, effect, signal } from "@preact/signals-react";
import dayjs from "dayjs";
import { JSONPath } from "jsonpath-plus";
import {
  clone,
  get,
  has,
  isArray,
  isBoolean,
  isDate,
  isEmpty,
  isEqual,
  isNil,
  isNumber,
  isObject,
  isString,
  set,
  some,
} from "lodash-es";
import { ServiceConfig, Subscription } from "nats.ws";
import { Location } from "react-router-dom";
import { CustomLabel, Menu, Service, SiteConfiguration } from "../../resolvers-types.ts";
import { Result } from "../models/result";
import { guiActions } from "../sampleData/editor-gui/editor-gui.ts";
import { executeAction } from "./actions";
import { UserAction } from "./analytics.ts";
import AppError from "./appError.ts";
import { getUserProfile } from "./auth.ts";
import { designerState } from "./designer.ts";
import { getAst, getExprValue, isStateVar, setExprValue } from "./interpreter.js";
import { log } from "./logger.js";
import { ItemNode, buildMetadataMap, createItemNode, getTypename, isComponentGuiRoute } from "./metadataUtils.js";
import { client, forceHttpProtocol } from "./nats-client";
import { RuntimeContext } from "./NGFieldExtensions.js";
import { permissionMap } from "./security.ts";
import { setLocalSetting } from "./settingsManager.ts";
import tracing from "./tracing.js";
import {
  boolOrDefault,
  createOptionalDebouncedFunction,
  isNullOrEmpty,
  isNullOrUndefined,
  makeSlug,
  nullOrUndefinedOrDefault,
  numberOrDefault,
  removeAllBodyThemes,
} from "./utils.js";

type Binding = { [key: string]: string };

const tag = "service";

export interface Filter {
  Name: string;
  Value: string | number | boolean | string[] | number[]; // Adjust based on expected types
  Operator: string;
  Format?: string;
}

// Define the Aggregate type
interface Aggregate {
  [key: string]: string[]; // Each key maps to an array of strings
}

// Define the main QueryRequest type
export interface IQuery {
  Filter?: Filter[];
  GroupBy?: string[];
  OrderBy?: string[];
  Aggregate?: Aggregate;
  OData?: { [key: string]: string };
}

type Field = {
  Name: string;
  Type: string;
  Value: any;
  Required?: boolean;
  Binding?: Binding | string;
  Bindings?: Binding;
  Skip: boolean;
};

export const NGNextPage: Signal<string | null> = signal(null);

const localeUnset = "";
const currencyUnset = "";
const baseLocale = "en-US"; // Default locale for the application

export type GlobalStateType = {
  Permissions: Signal<any>;
  Menu: Signal<Menu | null>;
  ReleaseInfo: Signal<any>;
  TenantConfig: Signal<any>;
  User: Signal<any>;
  NGService: { [key: string]: any };
  NGSite: Signal<any>;
  NGTheme: Signal<any>;
  NGTemplate: Signal<any>;
  Locale: Signal<string>;
  DefaultCCY: Signal<string>;
  Impersonation: Signal<any>;
  Translations: Signal<{ [key: string]: any }>;
  ThemeMode: Signal<string | null>;
  IsEmbeded: Signal<boolean>;
  NGSampleDataServices: Signal<any>;
  NGDependencyInjectionServices: Signal<any>;
};

// DO NOT EXPORT!
let globalState: GlobalStateType = {
  Permissions: signal(null),
  Menu: signal<Menu | null>(null),
  ReleaseInfo: signal(null),
  TenantConfig: signal(null),
  User: signal(null),
  NGService: {},
  NGSite: signal(null),
  NGTheme: signal(null),
  NGTemplate: signal(null),
  Locale: signal(localeUnset),
  DefaultCCY: signal(currencyUnset),
  Impersonation: signal(null),
  Translations: signal({}),
  ThemeMode: signal(null),
  IsEmbeded: signal(false),
  NGSampleDataServices: signal(null),
  NGDependencyInjectionServices: signal(null),
};

export function clearState(url) {
  globalState = {
    Permissions: globalState.Permissions ?? signal(null),
    NGService: {},
    NGTemplate: globalState.NGTemplate ?? signal(null),
    NGTheme: globalState.NGTheme ?? signal(null),
    NGSite: globalState.NGSite ?? signal(null),
    User: globalState.User ?? signal(null),
    Menu: globalState.Menu ?? signal(null),
    ReleaseInfo: globalState.ReleaseInfo ?? signal(null),
    TenantConfig: globalState.TenantConfig ?? signal(null),
    Locale: globalState.Locale ?? signal(localeUnset),
    DefaultCCY: signal(currencyUnset),
    Impersonation: globalState.Impersonation ?? signal(null),
    Translations: globalState.Translations ?? signal({}),
    ThemeMode: signal(null),
    IsEmbeded: signal(false),
    NGSampleDataServices: globalState.NGSampleDataServices ?? signal(null),
    NGDependencyInjectionServices: globalState.NGDependencyInjectionServices ?? signal(null),
  };
  iframeState = {};
  initStateFromUrl(new URL(url).searchParams);
}

export function getExprForElement(expr, elementId, event) {
  const node = meta.getNode(elementId);
  const context = (node?.context as RuntimeContext) ?? getDefaultContext();
  const config = node?.config;
  const { state, form, parentState } = getState(context);
  const scope = getScope(context, config, state, form, event, parentState);
  return getExprValue(expr, scope, null, false);
}

// Expose some details to the outside
window.jbi = {
  getState: () => getObjectData(globalState),
  getMeta: () => Object.fromEntries(metadataMap.entries()),
  getExprValue: getExprForElement,
  forceHttpProtocol,
};

export let iframeState = {};

const metadata = signal({});
const metadataMap = new Map<string, ItemNode>();

export function getUserProfileTimeZone() {
  return globalState.User?.value?.Details?.TimeZone;
}

export function getBrowserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getNode(id) {
  return metadataMap.get(id) ?? null;
}

function getParent(id) {
  return getNode(getNode(id)?.parent) ?? null;
}

export const meta = {
  version: signal(0),
  size: () => metadataMap.size,
  addNode: (config, context) => {
    let node = getNode(config.Id);
    if (node) {
      node.context = context;
      return node;
    }

    let parentCtx = context.Path[context.Path.length - 2] ?? null;
    if (parentCtx?.__typename === "Index") parentCtx = context.Path[context.Path.length - 3] ?? null;

    const parent = metadataMap.get(parentCtx?.Id);

    if (parent == null) node = createItemNode(config, null, 0, [0], [config.Id], context);
    else node = createItemNode(config, parent, parent.level, parent.path, parent.pathIds, context);

    metadataMap.set(config.Id, node);

    return node;
  },
  getAll: () => metadataMap.entries(),
  getNode,
  getParent,
  clear: () => metadataMap.clear(),
  build: (page: any) => {
    if (page == null) return;
    metadataMap.clear();
    buildMetadataMap(metadataMap, page, null, 0, [0], [page.Id]);
    meta.version.value = meta.version.peek() + 1;
  },
  addReference: (reference, component: any) => {
    const refNode = metadataMap.get(reference.Id);
    if (refNode == null) {
      log.error("meta", "Reference not found");
      return;
    }
    buildMetadataMap(metadataMap, component, reference, refNode.level, refNode.path, refNode.pathIds);
    meta.version.value = meta.version.peek() + 1;
  },
};

export function GetAllCustomLabelsFromSite() {
  const site = GetSite();

  if (isNil(site) || isNil(site.CustomLabels)) {
    return [];
  }

  return site.CustomLabels as CustomLabel[];
}

export function GetCustomLabelFromSite(customLabelName: string) {
  const site = GetSite();

  if (isNil(site) || isNil(site.CustomLabels) || isNil(site.CustomLabels[customLabelName])) {
    return null;
  }

  return site.CustomLabels[customLabelName];
}

export function GetFormatFromSite(formatName: string) {
  const site = GetSite();

  if (isNil(site) || isNil(site.Formats) || isNil(site.Formats[formatName])) {
    log.error("GetFormat", `The format '${formatName}' cannot be found in the current site`);
    return null;
  }

  return site.Formats[formatName];
}

export function GetFormatsFromSite() {
  const site = GetSite();

  return site?.Formats ?? {};
}

function lowercaseFirstLetter(s) {
  if (!s) return ""; // Handle empty or null
  return s?.charAt(0).toLowerCase() + s.slice(1);
}

let _LabelsForListFromSiteSettings: { [key: string]: string } | null = null;

export function GetConvertedLabelsForListFromSiteSettings(): { [key: string]: string } {
  if (_LabelsForListFromSiteSettings != null) return _LabelsForListFromSiteSettings;

  const site = GetSite();
  _LabelsForListFromSiteSettings = {};

  if (!isNil(site) && !isNil(site.Settings) && !isNil(site.Settings.ListAndChartLabels)) {
    Object.entries(site.Settings.ListAndChartLabels).forEach(([key, value]) => {
      if (_LabelsForListFromSiteSettings) _LabelsForListFromSiteSettings[lowercaseFirstLetter(key)] = value as any;
    });
  }

  return _LabelsForListFromSiteSettings as { [key: string]: string };
}

export function GetCustomLabelForListFromSiteSettings(customLabelName: string) {
  const site = GetSite();

  if (
    isNil(site) ||
    isNil(site.Settings) ||
    isNil(site.Settings.ListAndChartLabels) ||
    isNil(site.Settings.ListAndChartLabels[customLabelName])
  ) {
    return customLabelName;
  }

  return site.Settings.ListAndChartLabels[customLabelName];
}

export function GetSettingFromSite(settingName: string) {
  const site = GetSite();

  if (isNil(site) || isNil(site.Settings) || isNil(site.Settings[settingName])) {
    log.error("GetSetting", `The setting '${settingName}' cannot be found in the current site`);
    return null;
  }

  return site.Settings[settingName];
}

export function GetSettingsFromSite() {
  const site = GetSite();

  return site?.Settings ?? {};
}

export function ClearSite() {
  batch(() => {
    globalState.NGTheme.value = null;
    globalState.NGSite.value = null;
  });
}
export function ClearTheme() {
  batch(() => {
    globalState.NGTheme.value = null;
    globalState.NGSite.value = { ...globalState.NGSite.value, Theme: null, ParentTheme: null };
  });
}
export function GetTheme(): any {
  return globalState.NGTheme.value;
}
export function SetTheme(theme: any) {
  globalState.NGTheme.value = theme;
}

export function ClearTemplate() {
  batch(() => {
    globalState.NGTemplate.value = null;
    globalState.NGSite.value = { ...globalState.NGSite.value, Template: null };
  });
}
export function GetTemplate(): any {
  return globalState.NGTemplate.value;
}
export function SetTemplate(template: any) {
  globalState.NGTemplate.value = template;
}

export function ClearSampleDataServices() {
  batch(() => {
    globalState.NGSampleDataServices.value = null;
  });
}
export function GetSampleDataServices(): any {
  return globalState.NGSampleDataServices.value;
}
export function SetSampleDataServices(sampleDataServices: any) {
  globalState.NGSampleDataServices.value = sampleDataServices;
}

export function ClearDependencyInjectionServices() {
  batch(() => {
    globalState.NGDependencyInjectionServices.value = null;
  });
}
export function GetDependencyInjectionServices(): any {
  return globalState.NGDependencyInjectionServices.value;
}
export function SetDependencyInjectionServices(dependencyInjectionServices: any) {
  globalState.NGDependencyInjectionServices.value = dependencyInjectionServices;
}

export function GetSite(): SiteConfiguration {
  return globalState.NGSite.value;
}
export function SetSite(site: any) {
  batch(() => {
    globalState.NGSite.value = site;
    SetTheme(site.Theme);
  });
}

export function isEmbeded() {
  return globalState.IsEmbeded.value;
}

export function setThemeMode() {
  const userThemeMode = globalState.User.value?.Details?.ThemeMode ?? null;
  const defaultThemeMode = globalState.NGTheme.value?.ThemeMode ?? null;
  const themeMode = userThemeMode ?? defaultThemeMode ?? "light"; // Default to "light" if all are null
  globalState.ThemeMode.value = themeMode;

  if (!globalState.IsEmbeded.value) {
    setLocalSetting("theme", "mode", themeMode);
    removeAllBodyThemes();
    document.body.classList.add(`${themeMode}-theme`); // do not want to use this in embedmode
  }
}

export function getThemeMode() {
  return globalState.ThemeMode.value;
}

export async function GetUserProfileLocal(userId, context: RuntimeContext) {
  // log.info(tag, "Service request", "userprofile.get", userId);

  //const { global } = getState(context);

  if (globalState["User"]?.value?.Details) return globalState["User"].value;

  const userProfileResp = await getUserProfile(userId, context);

  // const userProfileResp = (await client.request("userprofile.get", {
  //   Id: userId,
  // })) as Result<any>;
  if (!userProfileResp?.success) {
    // log.error(tag, "Service userprofile.get failed", userProfileResp?.reasons);
    return false;
  }

  return userProfileResp?.data;
}

export async function GetSiteFromServer(context: RuntimeContext) {
  if (!isNil(GetSite())) return GetSite();

  log.info(tag, "Site get request", "site.get");

  try {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const siteId = urlParams.get("site") ?? globalState.User.value?.Details?.Site;
    const resp = await callServiceLight(
      { Name: "site.get", Service: "site.get" },
      { Id: siteId || null, Url: window.location.href },
      context
    );
    if (!resp?.success) {
      return false;
    }

    if (!isNil(resp?.data.Site)) SetSite(resp?.data.Site);

    setThemeMode();

    getLocale();
    getDefaultCCY();

    return resp?.data.Site;
  } catch (error) {
    log.error(tag, "Service site.get failed", error);
    return false;
  }
}

export async function GetThemeFromServer(context: RuntimeContext) {
  // if we have it in cache, fetch it from there to avoid a network call
  if (!isNil(GetTheme())) return GetTheme();
  const site = await GetSiteFromServer(context);

  if (isNil(site)) {
    log.error(tag, "Site not found.  Ensure there is a default site in the project.");
    return null;
  }

  if (isNil(site.Theme) && isNil(site.ThemeName)) {
    log.error(tag, "Site does not have a theme.  Ensure there is a default theme in this site: '" + site.Id + "'");
    return null;
  }

  if (isObject(site.Theme)) {
    SetTheme(site.Theme);
    return site.Theme;
  }

  log.info(tag, "Theme request", "project.theme.get", site.ThemeName);

  const themeSlug = makeSlug(site.ThemeName);

  const resp = (await client.request("project.theme.get", {
    Id: themeSlug,
  })) as Result<any>;
  if (!resp?.success) {
    log.error(tag, "Service project.theme.get failed", resp?.reasons);
    return false;
  }

  if (!isNil(resp?.data.Entity)) SetTheme(resp?.data.Entity);

  return resp?.data.Entity;
}

export async function GetTemplateFromServer(context: RuntimeContext) {
  // if we have it in cache, fetch it from there to avoid a network call
  if (!isNil(GetTemplate())) return GetTemplate();
  const site = await GetSiteFromServer(context);

  if (isNil(site)) {
    log.error(tag, "Site not found.  Ensure there is a default site in the project.");
    SetTemplate(null);
    return null;
  }

  if (isNil(site.Template) && isNil(site.TemplateName)) {
    log.error(
      tag,
      "Site does not have a template.  Ensure there is a default template in this site: '" + site.Id + "'"
    );
    SetTemplate(null);
    return null;
  }

  if (isObject(site.Template)) {
    SetTemplate(site.Template);
    return site.Template;
  }

  const templateSlug = makeSlug(site.TemplateName);
  const resp = await callServiceLight(
    { Name: "project.template.get", Service: "project.template.get" },
    { Id: templateSlug },
    context
  );
  if (!resp?.success) {
    SetTemplate(null);
    return false;
  }

  if (!isNil(resp?.data.Entity)) SetTemplate(resp?.data.Entity);
  return resp?.data.Entity;
}

async function GetSampleDataForServiceFromServer(serviceId: string, context: RuntimeContext) {
  return await CallDependencyInjectionGet(serviceId, "SampleData", context);
}

async function GetConfigForInjectedServiceFromServer(serviceId: string, context: RuntimeContext) {
  return await CallDependencyInjectionGet(serviceId, "Service", context);
}

async function CallDependencyInjectionGet(serviceId: string, entryType: string, context: RuntimeContext) {
  const params = { Id: serviceId, ThemeId: GetTheme()?.Id, EntryType: entryType };

  if (context?.Path?.length > 0) {
    const currentPath = context.Path[context.Path.length - 1];
    if (currentPath?.__typename === "Component") {
      params["ComponentId"] = (currentPath?.Config as any)?.ComponentId ?? currentPath?.Id;
    }
    if (currentPath?.__typename === "Page") params["PageId"] = currentPath?.Id;
  }

  if (!context?.Path) {
    return null;
  }

  const resp = await callServiceLight(
    { Name: "dependencyinjection.get", Service: "dependencyinjection.get" },
    params,
    context
  );
  if (!resp?.success) {
    log.info(tag, `'${entryType}' for service '${serviceId}' not found.`);
    return null;
  }

  return resp?.data?.Data ?? null;
}

function GetInjectedService(serviceId: string, context: RuntimeContext) {
  const diServices = GetDependencyInjectionServices();

  if (isNil(diServices) || isEmpty(diServices)) return null;

  for (const [key, value] of Object.entries(diServices)) if (serviceId === key) return value;

  return null;
}

export async function GetSampleDataServicesFromServer(context: RuntimeContext) {
  // if we have it in cache, fetch it from there to avoid a network call
  if (!isNil(GetSampleDataServices())) return;

  const resp = await callServiceLight(
    { Name: "dependencyinjection.query", Service: "dependencyinjection.query" },
    {},
    context
  );
  if (!resp?.success) {
    SetSampleDataServices(null);
    SetDependencyInjectionServices(null);
    return false;
  }

  const sampleDataServices = resp.data.SampleDataServices;
  if (isNil(sampleDataServices)) {
    log.warn(tag, "Sample Data Services not found.");
  }

  const dependencyInjectionServices = resp.data.DependencyInjectionServices;
  if (isNil(dependencyInjectionServices)) {
    log.warn(tag, "Dependency Injection Services not found.");
  }

  SetSampleDataServices(sampleDataServices);
  SetDependencyInjectionServices(dependencyInjectionServices);
}

function mapBindings(f: Field, scope: any, acc: any, isValid: boolean, fromFilters = false): boolean {
  if (f.Bindings) {
    for (const [k, b] of Object.entries(f.Bindings)) {
      const kv = !fromFilters ? (k === "Value" ? f.Name : k) : k;
      if (b == "Form" && !isNil(scope.Form)) {
        set(acc, kv, getFormData(scope.Form));
      } else {
        set(acc, kv, getExprValue(b, scope, null));
      }
    }
  } else {
    if (!fromFilters) acc[f.Name] = f.Value;
  }
  let hasParam = true;
  if (f.Required) {
    hasParam = !fromFilters
      ? acc[f.Name] !== null && acc[f.Name] !== undefined && !isNullOrEmpty(acc[f.Name])
      : acc.Value !== null && acc.Value !== undefined && !isNullOrEmpty(acc.Value);
  }
  isValid &&= hasParam;
  return isValid;
}

export function mapFields(fields: Field[], scope): [IQuery, boolean] {
  let isValid = true;

  function applySkip(x) {
    return x.Skip != undefined ? !getExprValue(x.Skip, scope, false) : true;
  }

  const params = fields.filter(applySkip).reduce((acc, f) => {
    if (f.Name == "Filter") {
      let value = f.Value;
      if (f.Bindings?.Value) {
        value = getExprValue(f.Bindings.Value, scope, []);
      }
      if (isArray(f.Value)) {
        const filterParams = value.filter(applySkip).map((f) => {
          const newVal = {
            Name: f.Name,
            Value: f.Value,
            Operator: f.Operator ?? "eq",
            OrConditionGroup: f.OrConditionGroup ?? null,
          };
          isValid = mapBindings(f, scope, newVal, isValid, true);
          return newVal;
        });
        acc["Filter"] = filterParams;
        return acc;
      }
    }
    // Special handling for OData to map all bindings into the same object, keep old behavior when Bindings.Value is set
    if (f.Name == "OData" && !f.Bindings?.Value) {
      const newVal = f.Value ? clone(f.Value) : {};
      isValid = mapBindings(f, scope, newVal, isValid, true);
      acc["OData"] = newVal;
      return acc;
    }

    isValid = mapBindings(f, scope, acc, isValid);

    return acc;
  }, {});
  // console.log("callService params", params, isValid);
  return [params, isValid];
}

export const defaultContext = {
  path: [],
};

export function getParams(s, scope): any {
  let [params, isValid] = mapFields(s.Fields, scope);
  if (s.Require) {
    isValid &&= getExprValue(s.Require, scope, null);
  }
  return [params, isValid];
}

export function getScope(context: RuntimeContext, config: any, state: any, form: any, event: any, parent: any) {
  const scope = {
    Global: globalState,
    State: state,
    Event: event ?? context.Row,
    Form: form,
    Config: config,
    Repeater: context.Repeater,
    Row: context?.Row,
    RowIndex: context?.Index,
    Grid: context.Grid,
    FormData: context.Form?.Data.value,
    Parent: parent,
  };
  return scope;
}

export function mapStreamResult(config, scope, data) {
  scope.Result = data;
  for (const [k, v] of Object.entries(config.StreamBindings ?? {})) {
    const src = getExprValue(v, scope, null);
    setExprValue(k, scope, src);
  }
}

export function mapServiceResult(config, scope, data) {
  scope.Result = data;
  for (const [k, v] of Object.entries(config.Bindings ?? {})) {
    const src = getExprValue(v, scope, null);
    setExprValue(k, scope, src);
  }
}

export function getServiceResult(config, scope, data) {
  scope.Result = data;
  const result = {};
  for (const [k, v] of Object.entries(config.Bindings ?? {})) {
    const src = getExprValue(v, scope, null);
    result[k] = src;
  }
  return result;
}

export async function callServiceLight(
  config,
  params,
  context,
  options?: CallServiceOptions
): Promise<{ success: boolean; data?: any; error?: any }> {
  const service = { Loading: signal(false), State: signal(null) };
  const { state, form, parentState } = getState(context);
  const scope = getScope(context, config, state, form, {}, parentState);
  return callServiceInternal(config, params, service, context, scope, options);
}

function isCoreService(service: string): boolean {
  return !service.startsWith("plugin.");
  // return (
  //   service.startsWith("project.") ||
  //   service.startsWith("site.") ||
  //   service.startsWith("userprofile.") ||
  //   service.startsWith("theme.") ||
  //   service.startsWith("permission.")
  // );
}

export function cancelStream(serviceName: string, requestId?: string) {
  // Cancel stream subscription (uses config.Name as key)
  cancelServiceStream(serviceName);

  // Cancel the ongoing service request (uses same key)
  cancelServiceRequest(serviceName, requestId);
}

// New function to cancel just the request without affecting the stream
export function cancelServiceRequest(serviceEndpoint: string, requestId?: string) {
  const abortKey = getAbortKey({ name: serviceEndpoint, requestId });
  if (abortKey && abortControllers[abortKey]) {
    log.info(tag, `Cancelling ongoing request for service ${serviceEndpoint}`);
    abortControllers[abortKey]?.abort();
    abortControllers[abortKey] = null;
  }
}

// New function to cancel just the stream without affecting the ongoing request
export function cancelServiceStream(serviceName: string) {
  if (subscriptions[serviceName]) {
    log.info(tag, `Cancelling stream subscription for service ${serviceName}`);
    subscriptions[serviceName]?.unsubscribe();
    subscriptions[serviceName] = null;
  }
}

function getAbortKey({ name, requestId }: { name: string; requestId?: string }) {
  return requestId ? `${name}:${requestId}` : undefined;
}

const subscriptions: Record<string, Subscription | null> = {};
const abortControllers: Record<string, AbortController | null> = {};

function interceptVersionedServiceError(config, params, reasons) {
  if (config.Service.startsWith("project.")) {
    if (config.Service.endsWith(".save")) {
      if (params.Entity) {
        if (isNil(globalState["NGVersions"])) {
          globalState["NGVersions"] = {};
        }

        delete globalState["NGVersions"][params.Entity.Id];
        params.Entity.NGVersion = -1;

        // if the reasons array contains any string with the word stale
        if (Array.isArray(reasons) && reasons.some((r) => r.message?.indexOf("stale") !== -1)) {
          // If it does, we need to mark the entity as stale
          // join the messages with a newline
          alert(reasons.map((r) => r.message).join("\n"));

          //alert(reasons.join("\n"));
        }
      }
    }
  }
}

async function retrieveFromSampleData(config, params, context) {
  const servicesWithSampleData = GetSampleDataServices();

  if (!servicesWithSampleData?.includes(config.Id)) return { hasSampleData: false, sampleData: null };

  const sampleData = await GetSampleDataForServiceFromServer(config.Id, context);

  if (isNil(sampleData)) return { hasSampleData: false, sampleData: null };
  return { hasSampleData: true, sampleData: sampleData };
}

// {
//     "InjectedServiceId": "plugin.dependency-injection-test-1",
//     "UseServiceConfig": false
// }

async function retrieveFromInjectedService(serviceId, params, context) {
  return await GetConfigForInjectedServiceFromServer(serviceId, context);
}

function interceptVersionedService(config, params) {
  if (config.Service.startsWith("project.")) {
    if (config.Service.endsWith(".save")) {
      if (params.Entity) {
        if (isNil(globalState["NGVersions"])) {
          globalState["NGVersions"] = {};
        }

        if (isNil(globalState["NGVersions"][params.Entity.Id])) {
          globalState["NGVersions"][params.Entity.Id] = params.Entity.NGVersion ?? 0;
        }

        globalState["NGVersions"][params.Entity.Id]++;
        params.Entity.NGVersion = globalState["NGVersions"][params.Entity.Id];
      }
    }
  }
}

export async function callServiceInternal(
  originalConfig,
  params,
  service,
  context,
  scope,
  options?: CallServiceOptions
): Promise<{ success: boolean; data?: any; error?: any }> {
  function executeServiceAction(trigger: string, data: any) {
    const endAction = config.Actions?.filter((x) => x.Trigger == trigger);
    if (!isNil(endAction) && endAction.length > 0) {
      for (const a of endAction) {
        // On start action, the data that is passed to the event is the input parameters
        executeAction(a, null, data, context, options?.UserAction);
      }
    }
  }

  const tracingHeaders = tracing.getHeaders(options?.UserAction);
  const appHeaders = getAppHeaders(originalConfig);
  const start = performance.now();
  const isValid = true; // its true because we got here
  let config: any = { ...originalConfig };

  const injectedService = GetInjectedService(config.Service, context);

  if (!isNil(injectedService)) {
    config = await retrieveInjectedConfiguration(injectedService, config, params, context);
  }

  log.groupInfo(tag, config.Name, config.Service, isValid ? "Start" : "Skip");
  if (config.Injected === true)
    log.info(tag, `This is an injected service.  Original service was: ${originalConfig.Service}`, config);

  log.info(tag, "params", params);
  log.info(tag, "paramsValid", isValid);
  log.info(tag, "state", globalState);
  log.info(tag, "tracingHeaders", tracingHeaders);
  log.info(tag, "appHeaders", appHeaders);
  log.info(tag, "meta", metadataMap);
  log.groupEnd();

  executeServiceAction("onStart", params);
  service.State.value = "requesting";

  // Create AbortController for this request
  let abortController: AbortController | undefined;
  const abortControllerKey = getAbortKey({ name: config.Name, requestId: options?.RequestId });
  if (abortControllerKey) {
    abortController = new AbortController();
    abortControllers[abortControllerKey] = abortController;
  }

  const result: { success: boolean; data: Result<any> | null } = { success: true, data: null };

  // Code for sample data loaded from the repository
  const { hasSampleData, sampleData } = await retrieveFromSampleData(config, params, context);

  //const { hasSampleData, sampleData } = { hasSampleData: false, sampleData: null }; // Disable sample data for now

  if (hasSampleData) {
    result.data = resolveFiltersSampleData(sampleData, params);
  } else {
    // If we are injecting a service, this overrides the sample data option
    if (config.UseSampleData) {
      result.data = resolveFiltersSampleData(config.SampleData, params);
    } else if (config.Type == "External") {
      try {
        const baseHeaders = config.Headers ?? {};
        const headers = {
          ...baseHeaders,
          ...tracingHeaders,
          ...appHeaders,
        };

        const url = new URL(config.Service);
        url.search = new URLSearchParams(params).toString();
        const resp = await fetch(url, {
          headers,
          signal: abortController?.signal, // Add abort signal to fetch
        });
        result.data = await resp.json();

        // Clean up abort controller on success
        cancelServiceRequest(config.Name);
      } catch (e) {
        // Clean up abort controller
        cancelServiceRequest(config.Name);

        // Check if this was an abort
        if (e.name === "AbortError") {
          log.info(tag, config.Name, config.Service, "Request was cancelled");
          service.Loading.value = false;
          service.State.value = "cancelled";
          return { success: false, error: "Request cancelled" };
        }

        logFailure(start, e, tracingHeaders);
        service.Loading.value = false;
        service.State.value = "error";
        executeServiceAction("onFailure", e);
        return { success: false };
      }
    } else {
      const headers = {
        ...tracingHeaders,
        ...appHeaders,
      };

      // Handle service stream mode
      let streamSubject: string | null = null;
      if (config.Stream) {
        // Clean up existing stream subscription for this service (but not the abort controller)
        cancelServiceStream(config.Name);

        streamSubject = `stream.${config.Service}.${tracingHeaders["NG-RequestId"]}`;
        log.info(tag, config.Name, config.Service, ` subscribing to stream ${streamSubject}`);

        subscriptions[config.Name] = await client.subscribe(streamSubject, (msg: any) => {
          log.groupInfo(tag, config.Name, config.Service, "Stream received message");
          log.info(tag, "params", params);
          log.info(tag, "result", msg.data);
          log.info(tag, "state", globalState);
          log.info(tag, "tracingHeaders", tracingHeaders);
          log.info(tag, "appHeaders", appHeaders);
          log.info(tag, "meta", metadataMap);
          log.groupEnd();

          mapStreamResult(config, scope, msg.data);
        });
      }

      interceptVersionedService(config, params);

      try {
        const resp = (await client.request(
          config.Service,
          params,
          headers,
          config.Timeout ?? undefined,
          config.Retries ?? undefined,
          1000,
          abortController?.signal
        )) as Result<any>;

        // Clean up resources on successful request completion
        // cancelServiceRequest(config.Name);
        if (config.Stream) {
          cancelServiceStream(config.Name);
        }

        if (!resp?.success) {
          if (resp?.statusCode === 405) {
            throw new AppError(resp);
          }

          interceptVersionedServiceError(config, params, resp?.reasons);

          logFailure(start, resp?.reasons, headers);
          service.Loading.value = false;
          service.State.value = "error";
          executeServiceAction("onFailure", resp?.reasons);
          return { success: false, error: resp };
        }

        if (!isCoreService(config.Service)) {
          const locale = getLocale();
          if (locale != localeUnset && locale != baseLocale && !isNil(resp) && !isEmpty(resp.data)) {
            const translations = await getTranslations(locale);

            if (!isEmpty(translations)) {
              resp.data = applyTranslations(translations, resp.data);
            }
          }
        }

        result.data = resp?.data;
      } catch (e: unknown) {
        // Clean up both request and stream on error since the error might invalidate the stream
        cancelStream(config.Name);

        // Check if this was an abort
        if (e instanceof Error && e.name === "AbortError") {
          log.info(tag, config.Name, config.Service, "Request was cancelled");
          service.Loading.value = false;
          service.State.value = "cancelled";
          return { success: false, error: "Request cancelled" };
        }

        logFailure(start, e, headers);
        service.Loading.value = false;
        service.State.value = "error";
        executeServiceAction("onFailure", e);
        return { success: false };
      }
    }
  }

  executeServiceAction("onSuccess", { Result: result.data });
  const end = performance.now();
  log.groupInfo(tag, config.Name, config.Service, "End", ` took: ${end - start}ms`);
  if (config.Injected === true)
    log.info(tag, `This was an injected service.  Original service was: ${originalConfig.Service}`, config);

  log.info(tag, "params", params);
  log.info(tag, "result", result.data);
  log.info(tag, "state", globalState);
  log.info(tag, "tracingHeaders", tracingHeaders);
  log.info(tag, "appHeaders", appHeaders);
  log.info(tag, "meta", metadataMap);
  log.groupEnd();

  return result;

  function logFailure(start: number, error, headers) {
    const end = performance.now();
    log.groupError(tag, config.Name, config.Service, "Failed", ` took: ${end - start}ms`);
    if (config.Injected === true)
      log.info(tag, `This was an injected service.  Original service was: ${originalConfig.Service}`, config);

    log.info(tag, "params", params);
    log.info(tag, "error", JSON.stringify(error?.map ? error.map((e) => e.message || e) : error));
    log.info(tag, "state", globalState);
    log.info(tag, "headers", "RequestId:", headers["NG-RequestId"], "SessionId:", headers["NG-SessionId"]);
    log.info(tag, "meta", metadataMap);
    log.groupEnd();
  }
}

async function retrieveInjectedConfiguration(injectedService: any, config: any, params: any, context: any) {
  log.info(
    tag,
    `Possible Injected service. ${injectedService.InjectedServiceId} is a candidate to replace ${config.Service}`
  );
  const retrievedConfig = await retrieveFromInjectedService(config.Service, params, context);

  if (isNil(retrievedConfig) || isNullOrEmpty(retrievedConfig.InjectedServiceId)) {
    log.info(
      tag,
      `NO Injected service. No configuration found for service ${config.Service} with candidate ${injectedService.InjectedServiceId}. Using original config.`
    );
  } else {
    // We have a service to inject
    if (retrievedConfig?.UseServiceConfig === true) {
      log.info(tag, `Injected service. Replacing fill configuration for service ${config.Service}.`);
      config = { ...retrievedConfig, Injected: true };
    } else {
      log.info(
        tag,
        `Injected service. Replacing service name only for service ${config.Service} with new name ${retrievedConfig.InjectedServiceId}.`
      );
      config = {
        ...config,
        Service: retrievedConfig.InjectedServiceId,
        Id: retrievedConfig.InjectedServiceId,
        Name: retrievedConfig.InjectedServiceId,
        UseSampleData: false, // when injecting a service, we never use the local sample data
        Injected: true,
      };
    }
  }
  return config;
}

function resolveFiltersSampleData(sampleData, params) {
  if (isNullOrUndefined(sampleData.Items)) return sampleData;

  let result = sampleData.Items;
  if (isNullOrUndefined(params) || isEmpty(params)) return { Items: result };
  const filters = params.Filter;
  const select = params.Select;
  if (!isNullOrUndefined(select)) {
    result = result.map((r) => {
      const s = {};
      select.forEach((key) => {
        if (key in r) {
          s[key] = r[key];
        }
      });
      return s;
    });
  }

  if (isNullOrUndefined(filters)) return { Items: result };
  result = result.filter((d) => {
    return filters.every((f: Filter) => {
      if (isNullOrUndefined(f.Name) || !f.Value) return true;
      if (!f.Operator || f.Operator == "eq") return d[f.Name] == f.Value;
      if (f.Operator == "neq") return d[f.Name] != f.Value;
      if (f.Operator == "gt") return d[f.Name] > f.Value;
      if (f.Operator == "lt") return d[f.Name] < f.Value;
      if (f.Operator == "gte") return d[f.Name] >= f.Value;
      if (f.Operator == "lte") return d[f.Name] <= f.Value;
      if (f.Operator == "contains") return d[f.Name].includes(f.Value);
      if (f.Operator == "notcontains") return !d[f.Name].includes(f.Value);
      if (f.Operator == "startswith") return d[f.Name].startsWith(f.Value);
      if (f.Operator == "endswith") return d[f.Name].endsWith(f.Value);
      if (f.Operator == "in") return (f.Value as string[]).includes(d[f.Name]);
      if (f.Operator == "notin") return !(f.Value as string[]).includes(d[f.Name]);
      return true;
    });
  });

  return { Items: result };
}

const optionallyDebouncedCallServiceInternal = createOptionalDebouncedFunction(callServiceInternal);

export type CallServiceOptions = {
  UserAction?: UserAction;
};

export async function callService(
  config,
  context,
  force = false,
  event: any = null,
  state: any = null,
  form: any = null,
  scope: any = null,
  service: any = null,
  options?: CallServiceOptions
) {
  if (config.Trigger == "Action" && !force) return false;

  if (scope == null) {
    const contextState = getState(context);
    state = contextState.state;
    form = contextState.form;
    scope = getScope(context, config, state, form, event, contextState.parentState);

    if (isNil(service)) service = getService(context, config.Name);
  }

  if (config.ConfigBindings) {
    mapConfigBindings(config, scope);
  }

  const [params, isValid] = getParams(config, scope);

  const tracingHeaders = tracing.getHeaders(options?.UserAction);
  const appHeaders = getAppHeaders(config);

  if (!config.UseSampleData && !isValid) {
    log.groupInfo(tag, config.Name, config.Service, isValid ? "Start" : "Skip");
    log.info(tag, "params", params);
    log.info(tag, "paramsValid", isValid);
    log.info(tag, "state", globalState);
    // log.info(tag, "state", state);
    // log.info(tag, "form", form);
    log.info(tag, "tracingHeaders", tracingHeaders);
    log.info(tag, "appHeaders", appHeaders);
    log.info(tag, "meta", metadataMap);
    log.groupEnd();

    return false;
  }

  service.Loading.value = true;
  // await delay(1000);

  const shouldDebounce = !!config.Debounce;
  const delay = config.Debounce;
  const result = await optionallyDebouncedCallServiceInternal(
    shouldDebounce,
    delay,
    config,
    params,
    service,
    context,
    scope,
    options
  );

  if (!result.success) {
    const error: any = result.error;
    mapServiceResult(config, scope, error);
    return false;
  }
  let data: any = result.data;

  service.Loading.value = false;
  service.State.value = "success";
  service.Data.value = data;

  // transforms
  if (config.Mappings) {
    const srcTransform = mapJSONPath(config.Mappings.Source, data, {});
    const tgtTransform = mapJSONPath(config.Mappings.Target, srcTransform, {});
    data = { ...srcTransform, ...tgtTransform };
  }

  if (config.Bindings) {
    mapServiceResult(config, scope, data);
  } else if (!config.Form && data != null) {
    // update state
    console.warn("[DEPRECATED]: Bindings for the service are required");
    for (const [k, v] of Object.entries(data)) {
      if (state[k]) {
        state[k].value = v;
      } else {
        state[k] = signal(v);
      }
    }
  }

  return true;
}

export function logState(tag, name, context, componentId, formId) {
  // const { state, form } = getState(context, componentId, formId);
  log.groupInfo(tag, name);
  log.info(tag, "state", globalState);
  // log.info(tag, "state", state);
  // log.info(tag, "form", form);
  log.info(tag, "meta", metadataMap);
  // log.info(tag, "metadata", metadata);
  log.groupEnd();
}

function mapConfigBindings(config: any, scope: any) {
  for (const [k, v] of Object.entries(config.ConfigBindings)) {
    const src = getExprValue(v, scope, null);
    config[k] = src;
  }
}

export function initStateFromUrl(queryParams: URLSearchParams) {
  batch(() => {
    const scope = { State: globalState, Event: null, Form: null, Config: null };

    for (const [key, value] of queryParams.entries()) {
      let v = boolOrDefault(value, value);
      v = numberOrDefault(value, v);
      v = nullOrUndefinedOrDefault(value, v);
      setExprValue(`State.${key}`, scope, v);
    }
  });
}

export function getDefaultContext(): RuntimeContext {
  return { Path: [] };
}

function getPathId(context) {
  return context.path.map((x) => x.__typename + "[" + x.Id + "]").join(".");
}

export function getParentComponent(context) {
  for (let i = context.Path.length - 2; i >= 0; i--) {
    if (context.Path[i].__typename === "Component") {
      return context.Path[i].Config;
    }
  }
  return null;
}

export function setImpersonation(userId: string, tabId: string) {
  globalState.Impersonation.value = { UserId: userId, SessionIndex: tabId };
}

export function setupPage(config, context: RuntimeContext | null = null) {
  config ||= {};
  metadata.value = config;
  meta.build(config);
  context ||= getDefaultContext();

  return setupServices(config, context);
}

const serviceIntervals: { [key: string]: NodeJS.Timer } = {};

export function setupInitialState(config: any, context: RuntimeContext) {
  if (isNil(config.SampleData)) return;

  if (context.InDesignMode && designerState?.IgnoreSampleDataInEditor.value) {
    clearSampleDataFromState(config, context, "SampleData");
    return;
  }

  if (!context.InDesignMode || designerState?.IgnoreSampleDataInEditor.value)
    if (isNil(config.UseSampleDataToInitializeState) || !config.UseSampleDataToInitializeState) return;

  initializeState(config, context, "SampleData");
}

function clearSampleDataFromState(config, context, from) {
  config.State ||= {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { global, state, form, parentState } = getState(context);

  for (const [k, v] of Object.entries(config[from])) {
    delete state[k];
  }
}

function initializeState(config, context, from) {
  config.State ||= {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { global, state, form, parentState } = getState(context);
  const scope = getScope(context, config, state, form, null, parentState);

  for (const [k, v] of Object.entries(config[from])) {
    if (isSignal(state[k])) {
      if (!isReadonlySignal(state[k])) state[k].value = v;
    } else {
      state[k] = signal(v);
    }
  }
  return { state, scope, form };
}

export function setupServices(config: any, context) {
  config.Services ||= [];
  const { state, scope, form } = initializeState(config, context, "State");
  resolveBindings(config, scope);

  // var id = getPathId(context);
  // set(metadata, , meta);

  const cleanup = () =>
    Object.keys(serviceIntervals).forEach((s) => {
      clearInterval(serviceIntervals[s]);
      delete serviceIntervals[s];
    });

  state["NGService"] ||= {};
  for (const s of config.Services) {
    if (!isNil(state["NGService"][s.Name])) continue;

    if (typeof s !== "object" || !s.Name) {
      log.error(tag, "Error: Each element in meta.Services should be an object with a Name property.");
      continue;
    }

    // const ms = (metadata as any).Services.findIndex((x) => x.Name == s.Name);
    // if (ms < 0) (metadata as any).Services.push(s);

    state["NGService"][s.Name] = {
      Config: signal(s),
      Loading: signal(false),
      State: signal("initialized"),
      Progress: signal([]),
      Data: signal({}),
    };
    effect(() => {
      if (s.Trigger == "Subscribe") {
        log.debug(tag, "Subscribing to", s.Service, s.Name);
        client.subscribe(s.Service, (msg: any) => {
          log.groupInfo(tag, s.Name, s.Service, ` received message`);
          log.info(tag, "result", msg);
          log.info(tag, "state", state);
          log.info(tag, "form", form);
          log.groupEnd();
          mapServiceResult(s, scope, msg.data);
        });
        // } else if (s.Trigger == "Polling") {
        //   if (serviceIntervals[s.Service]) {
        //     log.debug(tagService, "Clearing ", s.Service);
        //     clearInterval(serviceIntervals[s.Service]);
        //   }
        //   callService(s, context);
        //   const unsub = setInterval(() => {
        //     callService(s, context);
        //   }, s.RefreshInterval);
        //   serviceIntervals[s.Service] = unsub;
      } else {
        callService(s, context);
      }
    });
  }

  return cleanup;
}

export function resolveBindings(config: any, scope: any) {
  if (!isObject(config.Bindings)) return;
  // const { state, form, parent } = getState(context);
  // const scope = getScope(context, config, state, form, {}, parent);
  for (const [k, v] of Object.entries(config.Bindings)) {
    if (isNil(v)) continue;
    const src = getExprValue(v, scope, null, false);
    setExprValue(k, scope, src);
  }
}

export function setupForm(context: RuntimeContext) {
  const extractNamesRecursive = (items) => {
    const uniqueNames = new Set();

    const nonControls = ["Container", "Layout"];
    const controls = ["StyleEditor"];

    items.forEach((item) => {
      const type = getTypename(item);
      const isControl = controls.includes(type) || !nonControls.some((x) => type.includes(x));

      if (isControl) {
        if (item.Name) uniqueNames.add(item.Name);
        // if (item.Bindings) {
        //   for (var [k, v] of Object.entries(item.Bindings)) {
        //     collectFormProperties(v).forEach((name) => uniqueNames.add(name));
        //   }
        // }
      }
      const children = item.Items ?? item.Groups ?? [];
      if (children.length > 0) extractNamesRecursive(children).forEach((name) => uniqueNames.add(name));
    });

    return [...uniqueNames];
  };

  effect(() => {
    const { form, formParent, state } = getState(context);

    const formId = context.Form?.Config.Id;
    globalState.FormId = signal(formId);

    // const formData = initAndGetFormDataById(formCtx, form);
    const stateData = context.Form?.Data.value;
    const controls = context.Form?.Config.Items;

    if (stateData == null && formParent?.state[formParent?.id] != null) {
      formParent.state[formParent.id] = {};
    }

    if (!stateData || !controls || isNil(form)) return;

    // Whenever form binding value changes, map it to the child values
    batch(() => {
      // const keys = formCtx.Form.Items.filter((x) => x.Name).map((x) => x.Name);

      // Get full set of keys across mapped object and controls.
      // We need to create a signal for each otherwise Form.Prop won't work properly.
      // const keys = union(extractNamesRecursive(controls), Object.keys(stateData));
      // const keys = union(Object.keys(stateData), extractNamesRecursive(controls));
      // const keys = extractNamesRecursive(state);
      const keys = extractNamesRecursive(controls);
      const formData = form;

      // AA-TODO: inspect metadata and map based on references
      for (const k of keys as string[]) {
        if (formData[k] == null) {
          const s = signal(null);
          formData[k] = s;
          effect(() => {
            const v = s.value;
            if (state?.SyncUpdate) state.SyncUpdate.value = { id: formId, key: k, value: v };
          });
        }
        // formData[k] ||= signal(null);
        const v = get(stateData, k as string);
        if (!isNil(v)) {
          formData[k].value = v;
        }
        // else if (isSignal(formData[k])) {
        //   formData[k].value = isArray(formData[k].peek()) ? [] : null;
        // }
      }

      // Create any missing properties based on control values
      // const newState = cloneDeep(stateData);
      // for (const k of keys) {
      //   const v = get(newState, k);
      //   if (v == undefined) {
      //     set(newState, k, null);
      //   }
      // }

      // Map state data into signal object
      // formParent.state[formParent.id] = objToSignalRec(newState);

      // for (const k of keys) {
      //   const v = get(stateData, k);
      //   if (v !== undefined) {
      //     form[k] ||= signal(v);
      //     form[k].value = v;
      //     continue;
      //   }

      //   const cv = controls.find((x) => x.Name == k);
      //   if (cv !== undefined) {
      //     form[k] ||= signal(cv.Value);
      //     form[k].value = cv.Value;
      //     continue;
      //   }

      //   if (isSignal(form[k])) {
      //     form[k].value = isArray(form[k].peek()) ? [] : null;
      //   }
      // }

      log.debug("state", "setupForm", context.Form, form, globalState);
    });
  });
}

export function setupIframe(iframeId, iframeRef) {
  iframeState[iframeId] = signal({
    ContentWindow: iframeRef.contentWindow,
    BoundingClientRect: iframeRef.getBoundingClientRect(),
  });
}

export function getIframeState(iframeId) {
  const iframe = iframeState[iframeId];
  if (isNil(iframe)) {
    return null;
  }
  return iframe.value;
}

export function clearFormById(formId) {
  const form = getFormState(formId);
  if (form) clearForm(form.object?.[formId]);
}

export function clearForm(form) {
  batch(() => {
    for (const [k, v] of Object.entries(form)) {
      if (form[k]) {
        form[k].value = isArray((v as any).value) ? [] : null;
      }
    }
  });
}

export function isSignal(obj: any): boolean {
  return !isNil(obj) && has(obj, "v");
}

export function isReadonlySignal(obj: any): boolean {
  return !isNil(obj) && has(obj, "x") && typeof obj.x === "function";
}

// ────────────── helper: detect a React/Preact Fiber object ──────────────
const isFiberNode = (o: any) =>
  !!o && typeof o === "object" && typeof o.tag === "number" && "child" in o && "return" in o;
// ────────────────────────────────────────────────────────────────────────

export function getObjectData(obj: any, maxArraySize: number | undefined = undefined) {
  // ─── primitives / nulls / simple types ────────────────────────────────
  if (obj == null || isDate(obj) || isString(obj) || isNumber(obj) || isBoolean(obj)) {
    return obj;
  }

  // ─── unwrap signals (but no cycle tracking) ───────────────────────────
  if (isSignal(obj)) {
    try {
      return getObjectData(obj.peek(), maxArraySize);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      return {
        Skipped: {
          reason: "SignalError while unwrapping",
          message: errorMessage,
        },
      };
    }
  }

  // ─── arrays ───────────────────────────────────────────────────────────
  if (isArray(obj)) {
    if (maxArraySize && obj.length > maxArraySize - 1) {
      const truncated = obj.slice(0, maxArraySize - 1).concat(`... total ${obj.length} items`);
      return truncated.map((item) => getObjectData(item, maxArraySize));
    }
    return obj.map((item) => getObjectData(item, maxArraySize));
  }

  // ─── plain objects (skip fibers & fiber back-pointers) ────────────────
  const result: any = {};

  for (const [key, value] of Object.entries(obj)) {
    // (A) skip DOM back-pointer → Fiber
    if (key.startsWith("__reactFiber$")) {
      result[key] = "[Skipped:ReactFiberRef]";
      continue;
    }

    // (B) skip if the value itself *is* a Fiber
    if (isFiberNode(value)) {
      result[key] = "[Skipped:FiberNode]";
      continue;
    }

    result[key] = getObjectData(value, maxArraySize);
  }

  return result;
}

export function getFormData(form, removeEmpty = false) {
  if (isNil(form)) return {};

  const result = {};

  for (const [k, v] of Object.entries(form)) {
    if (!isNil(v) && has(v, "v")) {
      let value = (v as any).value;

      if (isArray(value)) {
        // map values looking for signals
        value = value.map((x) => (isSignal(x) ? getFormData(x.value) : x));
      } else if (isObject(value)) {
        const entries = Object.entries(value);
        if (entries.length > 0) {
          const [fk, fv] = entries[0];
          const hasSignals = isSignal(fv);
          value = hasSignals ? getFormData(value) : value;
        }
      }

      // if you've changed it, we will set it every time
      // if you haven't touched it, we add the value only if it's not null or empty
      if (!(isNullOrEmpty(value) && (v as any).i === 0)) {
        if (value === "") set(result, k, null);
        else set(result, k, value);
      }

      if (removeEmpty) {
        if (isNullOrEmpty(value)) {
          delete result[k];
        }
      }
    }
  }

  return result;
}

export function setFormData(form, data) {
  if (isNil(data)) return;
  form ||= {};
  for (const [k, v] of Object.entries(data)) {
    if (form[k] && has(form[k], "v")) {
      form[k].value = v;
    } else {
      form[k] = signal(v);
    }
  }
  return form;
}

export function getRepeaterForm(repeater) {
  if (isNil(repeater) || !isArray(repeater)) return [];
  return repeater.map((r) => getFormData(r));
}

function objToSignalRec(value) {
  // Check if the value is a primitive or null
  if (value === null || typeof value !== "object") {
    return signal(value);
  }

  // If it's an array, map over the array and apply the function recursively
  if (Array.isArray(value)) {
    return value.map((item) => objToSignalRec(item));
  }

  // If it's an object, apply the function recursively to each property
  const result = {};
  for (const key in value) {
    result[key] = objToSignalRec(value[key]);
  }
  return result;
}

// function objToSignalRec(value) {
//   // Check if the value is a primitive, null, or an instance of signal (to avoid double wrapping)
//   if (value === null || typeof value !== "object" || value instanceof signal) {
//     return signal(value);
//   }

//   // If it's an array, map over the array and apply the function recursively
//   if (Array.isArray(value)) {
//     return signal(value.map((item) => objToSignalRec(item).value)); // Ensure each item is wrapped, then wrap the whole array
//   }

//   // If it's an object, apply the function recursively to each property
//   const result = {};
//   for (const key in value) {
//     result[key] = objToSignalRec(value[key]).value; // Wrap each property, but use .value to avoid double wrapping
//   }
//   return signal(result); // Finally, wrap the whole object
// }

function signalToObjRec(signalObj) {
  // Check if the value is a signal by checking for a .value property
  if (isSignal(signalObj)) {
    return signalToObjRec(signalObj.value);
  }

  // If it's an array, map over the array and apply the function recursively
  if (Array.isArray(signalObj)) {
    return signalObj.map((item) => signalToObjRec(item));
  }

  // If it's an object, apply the function recursively to each property
  if (signalObj !== null && typeof signalObj === "object") {
    const unwrappedObj = {};
    for (const key in signalObj) {
      unwrappedObj[key] = signalToObjRec(signalObj[key]);
    }
    return unwrappedObj;
  }

  // If it's a primitive value, just return it
  return signalObj;
}

function setValue(obj, path, newValue) {
  const keys = path.split(".").map((k) => k.replace(/\[(\d+)\]/, ".$1")); // Convert brackets to dot notation
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (i === keys.length - 1) {
      // Last key, update the value
      if (current[key] && typeof current[key].value !== "undefined") {
        current[key].value = newValue; // Assuming the new value is not wrapped
      } else {
        // If the key does not exist or is not a signal, wrap the new value
        current[key] = signal(newValue);
      }
    } else {
      // Navigate to the next level
      if (current[key] && typeof current[key].value !== "undefined") {
        current = current[key].value;
      } else {
        log.error(tag, "Path not found or not a signal:", path);
        return; // Path not valid or not a signal
      }
    }
  }
}

function isNumeric(str) {
  return !isNaN(str) && !isNaN(parseFloat(str)) && isFinite(str);
}

function getValue(obj, path, defaultValue) {
  const keys = path.split(".").map((k) => k.replace(/\[(\d+)]/, ".$1")); // Keep brackets notation converted to dot notation
  let current = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const isLastKey = i === keys.length - 1;

    if (isLastKey) {
      // Target property reached
      if (current[key] === undefined) {
        // Initialize with defaultValue
        current[key] = signal(defaultValue);
      }
      return current[key]; // Return the signal
    } else {
      // Intermediate navigation
      if (current[key] === undefined) {
        // Determine if the next key suggests an array or an object
        const nextKeyIsNumeric = isNumeric(keys[i + 1]);
        current[key] = signal(nextKeyIsNumeric ? [] : {});
      }
      current = current[key].value; // Proceed to next level
    }
  }

  return current; // Fallback return, should not be reached
}

export function setupLocalState(config, init, context: RuntimeContext, key: string | null = null) {
  if (isNil(config)) return { ...init };

  const tag = "state.setupLocal";
  const start = performance.now();

  // TODO: Check if this needs to do component as well
  if (config.IgnoreLocalState || context.InDesignMode || config.__typename == "Page") {
    for (const [k, v] of Object.entries(init || {})) {
      if (isSignal(v) && config[k] != null) {
        v.value = config[k];
      }
    }
  }

  const localState = { ...init };

  const { global, state, component, form, parentState } = getState(context);
  // const data = form ?? state;

  // For certain types map local state to global state as it needs to be shared
  const globalTypes = ["Dialog", "ContextMenu", "Snackbar"];
  if (globalTypes.includes(config.__typename)) {
    const group = "NG" + config.__typename;
    state[group] ||= {};
    state[group][config.Id] ||= {};
    const current = state[group][config.Id];
    for (const [k, v] of Object.entries(init || {})) {
      if (current[k] == undefined) {
        current[k] = v;
      } else {
        localState[k] = current[k];
      }
    }
  }

  const scope = getScope(context, config, state, form, {}, parentState);

  // Auto create a binding for form field if it's missing from config
  if (form && config?.Name) {
    const key = getTypename(config) == "Repeater" ? "Rows" : "Value";
    if (form[config.Name] == undefined) {
      form[config.Name] = init[key];
    } else if (isNullOrEmpty(form[config.Name]?.value) && !isNullOrEmpty(init[key]?.value)) {
      form[config.Name] = init[key];
    }
    localState[key] = form[config.Name];
  }

  for (const [k, v] of Object.entries((config.Bindings as Binding) || {})) {
    const defaultValue = init[k]?.value ?? init.DefaultValue?.value;
    const expr = v;

    if (isNullOrEmpty(v)) continue;

    let result = null;
    try {
      const ast: any = getAst(expr);

      // The only time we want to return a signal if we are just accessing top level State property
      // TODO: We may need this for Form as well
      const returnSignal = shouldReturnSignal(ast);

      result = returnSignal
        ? getExprValue(expr, scope, defaultValue, returnSignal)
        : computed(() => getExprValue(expr, scope, defaultValue));

      /* if (!returnSignal && config.CreateSignal && k == "DefaultValue") {
        localState["Value"] = signal(result.value);
      } */

      // Set form state to default value only if value hasn't been altered
      // or if value is bound to an expr
      if (form && !returnSignal && config?.Name) {
        if ((k == "DefaultValue" && !init.Value?.value) || k == "Value") {
          localState["Value"] = form[config.Name] = signal(result.value);
        }
      }
      if (returnSignal && result && result.i == 0) {
        effect(() => {
          const s = result;
          const v = s.value;
          if (s.i > 0) {
            const k = expr.substring(expr.indexOf(".") + 1);
            if (state.SyncStateUpdate) {
              state.SyncStateUpdate.value = { id: config.Id, key: k, value: v };
            }
          }
        });
      }
      localState[k] = result;
    } catch (e: any) {
      log.error(tag, "interpret error:", expr, e);
      result = null;
    }
  }
  const end = performance.now();

  // TODO: AA temporary disable this to unblock until correct solution is found.
  // applySecurity(localState, context, config);

  log.groupDebug(
    tag,
    (config.Typename ?? config.__typename ?? "") + " - ",
    config.UniqueName ?? config.Name ?? config.Label ?? config.Title ?? config.Id,
    `, took: ${end - start}ms`,
    window.location.href
  );
  log.debug(tag, "state", globalState);
  // log.debug(tag, "state", state);
  // log.debug(tag, "component", component);
  // log.debug(tag, "form", form);
  log.debug(tag, "local", localState);
  log.debug(tag, "meta", metadataMap);
  log.groupEnd();

  return localState;
}

const handlerNames: { [key: string]: string } = {
  processRowUpdate: "onRowUpdate",
};

type State = {
  global: GlobalStateType;
  state: any;
  component: any;
  form: any;
  current: any;
  formParent: any;
  parentState: any;
};

export function shouldReturnSignal(ast: any) {
  return (
    ast.type == "MemberExpression" &&
    ast.object?.type == "Identifier" &&
    isStateVar(ast.object?.name) &&
    ast.property?.type == "Identifier"
  );
}

export function isSimpleValue(ast: any) {
  return (
    ast.type == "Literal" ||
    (ast.type == "ArrayExpression" && ast.elements.length == 0) ||
    (ast.type == "ObjectExpression" && ast.properties.length == 0)
  );
}

function flattenPath(path: any[]): string {
  return "" + path.length + " " + path.map((x) => `${x.__typename}:${x.Id}`).join("^");
}

function printCallStack() {
  const error = new Error();
  return error.stack || "No stack trace available";
}

// export function addToContextPath(context: RuntimeContext, config: any, extra: any | null = null) {
//   const tag = "addToContextPath";
//   const tn = getTypename(config);

//   if (isNil(context)) {
//     log.error(tag, "Path no context", config, printCallStack());
//     return context;
//   }

//   const l = context.Path.length;
//   if (l > 0 && context.Path[l - 1].Id === config.Id && context.Path[l - 1].__typename === tn) {
//     context.Path[l - 1] = { ...context.Path[l - 1], ...extra };
//   }

//   return context;
// }

export function updateItemContext(
  context: RuntimeContext,
  config: any,
  extra: any | null = null,
  index: number | null = null
): RuntimeContext {
  const tag = "updateItemContext";
  const tn = getTypename(config);

  const toAdd = {
    Id: config.Id,
    __typename: tn,
    Config: config,
    ...extra,
  };

  if (isNil(context)) {
    log.error(tag, "Path no context", config, printCallStack());
    return context;
  }

  if (isNil(context.Path)) context.Path = [];

  const l = context.Path.length;

  if (l > 0 && context.Path[l - 1].Id === config.Id && context.Path[l - 1].__typename === tn) {
    const parentType = context.Path[l - 2]?.__typename;
    if (!(["Feed", "Reference"].includes(parentType) && tn == "Component")) {
      log.error(tag, "Duplicate item in path", context, config);
    }
    return context;
  }

  const newContext: RuntimeContext = {
    ...context,
    Path: [...context.Path, toAdd],
  };

  if (!isNil(index))
    newContext.Path.push({
      Id: index.toString(),
      __typename: "Index",
      Config: config,
    });

  // console.log("path", flattenPath(newContext.path));
  meta.addNode(config, newContext);

  return newContext;
}

// export function getComponent(id: string): any {
//   const components = getComponentState(id);
//   return components ? components[id] : null;
// }

// function getComponentState(id: string): any {
//   let currentState = globalState;

//   const components = currentState["NGComponent"];
//   if (!components) return null;
//   if (id in components) return components;

//   for (const v of Object.values(components)) {
//     currentState = v;
//     const components = currentState["NGComponent"];
//     if (components && id in components) return components;
//   }

//   return null;
// }
// actions
// - actions-save
//   - form-x
// - actions-requested
//   - actions-requested-sub
//      - actions-sub-sub

// export function getForm(id: string): any {
//   const forms = getFormState(id);
//   return forms ? forms[id] : null;
// }

// function findNGObjectById(obj: any, objectId: string, objectType: string): any | null {
//   if (obj?.[objectType] && obj[objectType].hasOwnProperty(objectId)) {
//     return obj;
//   }

//   if (obj?.NGComponent) {
//     for (const key of Object.keys(obj.NGComponent)) {
//       const result = findNGObjectById(obj.NGComponent[key], objectId, objectType);
//       if (result) return result;
//     }
//   }

//   return null;
// }

function findNGObjectByIdWithParent(
  structure: any,
  objectId: string | null,
  objectType: string
): { object: any; parent: any } | null {
  const stack = [{ node: structure, parent: structure }]; // Include parent information in the stack

  while (stack.length > 0) {
    const { node, parent } = stack.pop()!;

    // Check if this node contains the target object
    if (node?.[objectType] && node[objectType].hasOwnProperty(objectId)) {
      return { object: node[objectType], parent };
    }

    // Traverse only through NGComponent properties, keeping track of the current node as the parent
    if (node?.NGComponent) {
      Object.keys(node.NGComponent).forEach((key) => {
        stack.push({ node: node.NGComponent[key], parent: node });
      });
    }
  }

  return null;
}

const cleanObject = (obj: any, preserveKeys: string[], continueRecursing: string[]): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => cleanObject(item, preserveKeys, continueRecursing));
  } else if (typeof obj === "object" && obj !== null) {
    Object.keys(obj).forEach((key) => {
      if (!preserveKeys.includes(key)) {
        delete obj[key];
      } else {
        if (continueRecursing.includes(key)) obj[key] = cleanObject(obj[key], preserveKeys, continueRecursing);
      }
    });
  }
  return obj;
};

export function clearModalComponentState(context: RuntimeContext, modalId: string) {
  // const { state } = getState(context, null, null);
  // const componentIdToClear = context.Path.find((c) => c.Id === modalId)?.Config?.ContentContainer?.Items[0]
  //   ?.ReferenceId;
  // if (componentIdToClear && state["NGComponent"] && state["NGComponent"][componentIdToClear]) {
  //   state["NGComponent"][componentIdToClear] = cleanObject(
  //     state["NGComponent"]?.[componentIdToClear],
  //     ["NGService", "NGComponent", "NGForm", "NGDialog", "NGContextMenu"],
  //     ["NGComponent"]
  //   );
  // }
}

// function findNGObjectByIdImperative(structure: any, objectId: string, objectType: string): any | null {
//   const stack = [structure];
//   while (stack.length > 0) {
//     const current = stack.pop();
//     if (current?.[objectType] && current[objectType].hasOwnProperty(objectId)) {
//       return current[objectType];
//     }
//     if (current?.NGComponent) {
//       Object.values(current.NGComponent).forEach((value) => stack.push(value));
//     }
//   }
//   return null;
// }

export function getStateWithFallback(context, id, key, componentId = null, formId = null) {
  const { global, state } = getState(context, componentId, formId);
  if (state[key] && state[key][id]) {
    return state[key][id];
  } else {
    const { object } = getComponentState(componentId) ?? { object: null };
    if (object == null) return null;

    const state2 = object[componentId];
    if (state2[key] && state2[key][id]) {
      return state2[key][id];
    }
  }
  return global[key][id];
}

export function getModalPopup(context, id, componentId = null, formId = null) {
  // const { global, state } = getState(context, componentId, formId);

  if (isNullOrEmpty(componentId)) {
    for (let i = context.Path.length - 1; i >= 0; i--) {
      const tn = context.Path[i].__typename;

      if (tn == "Component") {
        const st = getStateWithFallback(context, id, "NGDialog", context.Path[i].Id, formId);

        if (!isNil(st)) return st;
      }
    }
  }

  return getStateWithFallback(context, id, "NGDialog", componentId, formId);
  // return findNGObjectByIdImperative(globalState, id, "NGDialog");
}

export function getContextMenu(context, id, componentId = null, formId = null) {
  return getStateWithFallback(context, id, "NGContextMenu", componentId, formId);
  // return findNGObjectByIdImperative(globalState, id, "NGContextMenu");
}

export function getService(context, id, componentId = null, formId = null) {
  const service = getStateWithFallback(context, id, "NGService", componentId, formId);

  if (isNil(service)) {
    const services = findNGObjectByIdWithParent(globalState, id, "NGService");

    return services?.object[id];
  }

  return service;
}

function getComponentState(id: string | null): any {
  return findNGObjectByIdWithParent(globalState, id, "NGComponent");
}

export function getFormState(id: string): any {
  return findNGObjectByIdWithParent(globalState, id, "NGForm");
}

function applySecurity(localState: any, context: RuntimeContext, config: any) {
  if (!localState["Disabled"]?.value) {
    localState["Disabled"] = computed(() => !isActionAllowed(context, config));
  }
}

function isActionAllowed(context, config): boolean {
  if (globalState.Permissions?.value == null) return true;
  const services = extractDistinctServiceNames(context, config);
  return services.every((service) => globalState.Permissions.value?.Services.includes(service) ?? false);
}

function extractDistinctServiceNames(context, config): string[] {
  const services = new Set<string>();
  const { controlToService, actionToService } = permissionMap;

  const control = getTypename(config);
  if (control != null && controlToService[control]) {
    services.add(controlToService[control]);
  }

  config.Actions?.forEach((action) => {
    action.CommandSet?.Commands?.forEach((command) => {
      const instructionName = command.Instruction?.Name;

      if (instructionName === "CallService") {
        // Extract ServiceName parameter for CallService instructions
        const serviceParam = command.Parameters?.find((param) => param.Name === "ServiceName");
        if (serviceParam) {
          const svc = getService(context, serviceParam.Value);
          if (svc != null) {
            services.add(svc.Config.value.Service);
          }
        }
      } else if (actionToService[instructionName]) {
        services.add(actionToService[instructionName]);
      }
    });
  });

  return Array.from(services);
}

export function getAppHeaders(config: any) {
  if (config.IgnoreLocale) return { ["Locale"]: "" };

  return {
    ["Locale"]: getLocale(),
  };
}

export const setDayjsLocale = async (globalLocale: string) => {
  try {
    let locale = globalLocale.toLowerCase();

    // iuf locale is empty does not load locale
    if (!locale) return;

    // There is a bug in DatePicker: if the locale doesn't define a specific format it breaks.
    // The "en" locale doesn't include this information, so we fallback to "en-us".
    if (locale?.toLocaleLowerCase() === "en") {
      locale = "en-us";
    }
    // load dynamically the locale file

    const localeData = await import(`./../../node_modules/dayjs/esm/locale/${locale}.js`);
    // set the locale json in dayjs mapping
    dayjs.Ls[locale] = localeData.default;
    // set locale
    dayjs.locale(locale);
  } catch (error) {
    log.error("setDayjsLocale", "Error loading locale data:", error);
  }
};

effect(() => {
  setDayjsLocale(globalState.Locale.value);
});

export function getLocale() {
  if (isNil(globalState)) return localeUnset;

  if (globalState?.Locale?.value == localeUnset) {
    if (globalState?.User?.value?.Details?.Locale) {
      globalState.Locale.value = globalState?.User?.value.Details?.Locale;
    } else {
      const site = GetSite();
      if (site?.DefaultLocale) {
        globalState.Locale.value = site.DefaultLocale;
      }
    }
  }

  return globalState?.Locale?.value ?? localeUnset;
}
export const applyTranslations = (translations: Record<string, string>, data: unknown): unknown => {
  const normalizeKey = (key: string): string =>
    Object.keys(translations).find((tKey) => tKey.toLowerCase() === key.toLowerCase()) ?? "";

  const preserveCase = (original: string, translated: string): string => {
    if (original === original.toUpperCase()) return translated.toUpperCase();
    if (original === original.toLowerCase()) return translated.toLowerCase();
    if (original[0] === original[0].toUpperCase()) {
      return translated[0].toUpperCase() + translated.slice(1);
    }
    return translated;
  };

  const translateText = (value: string): string => {
    const matchedKey = normalizeKey(value);
    if (!matchedKey) return value;
    return preserveCase(value, translations[matchedKey]);
  };

  const recurse = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      return node.map((item) => recurse(item));
    }

    if (typeof node === "object" && node !== null) {
      return Object.entries(node).reduce<Record<string, unknown>>((acc, [key, val]) => {
        acc[key] = typeof val === "string" ? translateText(val) : recurse(val);
        return acc;
      }, {});
    }

    if (typeof node === "string") {
      return translateText(node);
    }

    return node;
  };

  return recurse(data);
};

async function GetLocaleDataFromServer(locale: string): Promise<any> {
  const tag = "loadTranslations";

  log.info(tag, "Locale data get request", "project.locale.get");

  try {
    const start = performance.now();
    const resp = (await client.request("project.locale.get", {
      Id: locale.toLowerCase(),
    })) as Result<any>;
    const end = performance.now();
    log.info(tag, `project.locale.get took, ${end - start}ms`);

    if (!resp?.success) {
      log.error(tag, "Service Locale.get failed", resp?.reasons);
      return false;
    }

    return resp?.data.Entity;
  } catch (error) {
    log.error(tag, "Service project.locale.get failed", error);
    return false;
  }
}

async function getTranslations(locale: string) {
  if (isNil(globalState)) return {};

  const translations = globalState.Translations?.value ?? {};

  if (isNil(translations[locale])) {
    // Load translations for the specified locale
    const loadedTranslations = await GetLocaleDataFromServer(locale);
    translations[locale] = loadedTranslations.Translations ?? {};
  }

  return translations[locale] ?? {};
}

export function getDefaultCCY() {
  //return globalState?.DefaultCCY?.value ?? "USD";
  if (isNil(globalState)) return currencyUnset;

  // if (globalState?.DefaultCCY?.value == currencyUnset) {
  //   const site = GetSite();
  //   if (site?.DefaultCCY) {
  //     globalState.DefaultCCY.value = site.DefaultCCY;
  //   }
  // }

  // return globalState?.DefaultCCY?.value ?? currencyUnset;

  if (globalState?.DefaultCCY?.value == currencyUnset) {
    if (globalState?.User?.value?.Details?.DemoDefaultCCY) {
      globalState.DefaultCCY.value = globalState?.User?.value.Details?.DemoDefaultCCY;
    } else {
      const site = GetSite();
      if (site?.DefaultCCY) {
        globalState.DefaultCCY.value = site.DefaultCCY;
      }
    }
  }

  return globalState?.DefaultCCY?.value ?? currencyUnset;
}

export function getState(
  context: RuntimeContext,
  componentId: string | null = null,
  formId: string | null = null,
  force = false
): State {
  const global: GlobalStateType = globalState;
  let curState = global;
  let component: any = null;
  let form: any = null;
  let formParent: any = null;
  let parentState: any = null;

  for (let i = 0; i < context.Path.length; i++) {
    const { Id: id, __typename, IsForm: isForm, Name: name, Config } = context.Path[i];
    const type = isForm ? "Form" : __typename == "List" ? "Grid" : __typename;
    const group = "NG" + type;

    switch (type) {
      // case "ContextMenu":
      // case "Dialog":
      case "Form":
      case "Component":
        {
          if (type === "Form" && !isNil(form)) curState = component ?? global;

          let groupId = id;

          if (type == "Component") {
            const prev = context.Path[i - 1];
            if (
              prev != null &&
              prev.__typename == "Reference" &&
              prev.Config.ReferenceType == "Component" &&
              prev.Config.ReferenceId
            ) {
              groupId = `${prev.Config.ReferenceId}-${prev.Config.Id}`;
            }
          }

          curState[group] ||= {};
          curState[group][groupId] ||= {}; // Initialize if not already set.

          // Specific assignments based on the type.
          if (type === "Form") {
            formParent = { id: groupId, state: curState[group] };
            form = curState[group][groupId];
          } else if (type === "Component") {
            parentState = component;
            component = curState[group][groupId];
          }

          curState = curState[group][groupId]; // Move to the specific id in the group.
        }
        break;

      case "Grid":
      case "Repeater":
        {
          let collection;

          if (form != null && !Config.ExcludeFromForm) {
            // formState[parentFormId] ||= signal({});
            // const parentForm = formState[parentFormId].value;
            const fieldId = name ?? id;
            form[fieldId] ||= signal([]);
            form[fieldId].value ||= [];
            collection = form[fieldId].value;
            // parentForm[fieldId] ||= signal([]);
            // const repeaterState = parentForm[fieldId].value;
            // repeaterState[repeater.Index] ||= signal({});
            // formData = repeaterState[repeater.Index];
          } else {
            curState[group] ||= {};
            curState[group][id] ||= []; // Initialize as an array for 'Repeater'.
            collection = curState[group][id];
          }

          const index = context.Path[++i];
          if (index?.__typename === "Index") {
            const idx = parseInt(index?.Id, 10);
            collection[idx] ||= {}; // Ensure the specific index is initialized.

            const value = context.Path[++i];
            if (!isNil(value)) {
              const type2 = value.IsForm ? "Form" : value.__typename;
              // Handle the nested switch for 'Component' and 'Form' within 'Repeater'.
              switch (type2) {
                case "Component":
                  parentState = component;
                  component = collection[idx];
                  break;
                case "Form":
                  form = collection[idx];
                  formParent = { id: idx, state: collection };
                  break;
              }
            }
            curState = collection[idx]; // Move to the specific index in the repeater.
          }
        }
        break;
    }

    if (id === formId) break;
  }

  if (force || (componentId != null && isNil(component))) {
    // if (componentId != null) {
    const result = getComponentState(componentId);
    if (result != null) {
      parentState = result.parent;
      component = result.object[componentId as string];
    }
  }

  if (force || (formId != null && isNil(form))) {
    // if (formId != null) {
    const result = getFormState(formId);
    if (result != null) {
      parentState = result.parent;
      form = result.object[formId];
    }
  }

  const state = component ?? global;
  parentState ||= global;
  return { global, state, component, form, formParent, parentState };
}

function getHandlerName(trigger: string) {
  return handlerNames[trigger] || trigger;
}

export function setupHandlers(config, context: RuntimeContext, location?: Location) {
  const handlers = {};
  const actions = [...(config.Actions ?? [])]; // clone the array
  // Action is array, now check location
  if (isComponentGuiRoute(location)) {
    guiActions.forEach((action) => {
      const exists = some(actions, (item) => isEqual(item, action));
      if (!exists) actions.push(action);
    });
  }

  // this will execute for all parents, no matter their depth. For example, both a grandparent and a parent component will execute its actions if the grandchild triggers it.
  // const parentActions = context.Path.reduce((acc, x) => {
  //   if (x.Id=="Grouping"){ //.StopPropagation === true) {
  //     return acc;
  //   }
  //   if (x.Actions) {
  //     acc = acc.concat(x.Actions.filter((a) => a.Trigger.startsWith("onChild")));
  //   }
  //   return acc;
  // }, []);

  const parentActions: any[] = [];
  for (let i = context.Path.length - 1; i >= 0; i--) {
    const x = context.Path[i];

    if (x.Config.Actions) {
      const end = x.Config.__typename === "PropertiesEditor" ? 2 : 1;
      const pa = {
        Actions: [],
        Context: { ...context, Path: context.Path.slice(0, i + end) },
      };
      //pa.Actions = pa.Actions.concat(x.Config.Actions.filter((a) => a.Trigger.startsWith("onChild")));
      pa.Actions = pa.Actions.concat(x.Config.Actions.filter((a) => a?.ListenTo?.length > 0));
      parentActions.push(pa);
    }
    if (x.Id == "Grouping") break;
  }

  if (!isArray(actions)) return handlers;
  for (const a of actions) {
    const handlerName = getHandlerName(a?.Trigger);

    handlers[handlerName] = (e, e2, userAction, callback?) => {
      // const sysHandler = context.Handlers?.[handlerName];
      // if (sysHandler) {
      //   const stop = sysHandler(a, e, e2, null, context);
      //   if (stop) return;
      // }

      if (!isNil(config.getData)) {
        const d = config.getData(e, e2);

        if (a.preHandler) a.preHandler(handlerName, a, e, e2, d, context);
        executeAction(a, e, d, context, userAction).then(() => callback?.());
        if (a.postHandler) a.postHandler(handlerName, a, e, e2, d, context);
      } else {
        if (a.preHandler) a.preHandler(handlerName, a, e, e2, context);
        executeAction(a, e, e2, context, userAction).then(() => callback?.());
        if (a.postHandler) a.postHandler(handlerName, a, e, e2, context);
      }

      if (handlerName === "onClick") e?.stopPropagation?.();

      // execute parent actions
      //const event = a.Trigger.replace("on", "");
      for (const v of parentActions) {
        for (const pa of v.Actions) {
          //if (pa.Trigger == "onChild" + event) {
          if (pa?.ListenTo?.includes(a.Trigger)) {
            if (pa.preHandler) pa.preHandler(pa.Trigger, pa, e, e2, v.Context);
            executeAction(pa, e, e2, v.Context, userAction);
            if (pa.postHandler) pa.postHandler(pa.Trigger, pa, e, e2, v.Context);
          }
        }
      }
    };
  }
  return handlers;
}

function mapJSONPath(o, src, target) {
  return o
    ? Object.entries(o).reduce((acc, [k, v]: [string, any]) => {
      switch (typeof v) {
        case "object": {
          const data = JSONPath({ path: v.Selector, json: src, wrap: false });
          if (Array.isArray(data)) {
            acc[k] = data.map((oo) =>
              Object.entries(oo).reduce((accInner, [kk, vv]) => {
                if (v?.Map && v.Map[kk]) {
                  const m = v.Map[kk];
                  accInner[m.To] = m.Selector ? JSONPath({ path: m.Selector, json: vv as any, wrap: false }) : vv;
                } else if (v.IncludeAll) {
                  accInner[kk] = vv;
                }
                console.log("map inner obj", oo, kk, vv, accInner);
                return accInner;
              }, {})
            );
          } else {
            acc[k] = Object.entries(v.Map).reduce((accInner, [kk, vv]) => {
              accInner[kk] = JSONPath({ path: vv as any, json: data as any, wrap: false });
              console.log("map inner obj", data, kk, vv, accInner);
              return accInner;
            }, {});
          }
          break;
        }
        case "string":
        default:
          acc[k] = JSONPath({ path: v, json: src, wrap: false });
          break;
      }
      return acc;
    }, target)
    : target;
}

/**
 * Recursively evaluates and resolves "Bindings" within an object using a provided scope.
 *
 * This function creates a deep clone of the input object and traverses its properties.
 * When it encounters an object with a "Bindings" property, it evaluates each binding:
 * - If the binding value is "Form" and the scope contains a "Form" property, it replaces the target key with the form data.
 * - Otherwise, it evaluates the binding expression using `getExprValue`.
 * After processing, the "Bindings" property is removed from the object.
 * Arrays and nested objects are processed recursively.
 *
 * @param obj - The object to process for bindings.
 * @param scope - The scope used for evaluating binding expressions and form data.
 * @returns A new object with all bindings evaluated and replaced with their resolved values.
 */
export function evaluateObjectBindings({ obj, scope }: { obj: object; scope: any }) {
  function processObject(current: any): any {
    if (!isObject(current) || isArray(current)) {
      if (isArray(current)) {
        // Process arrays recursively, returning a new array
        return current.map((item: any) => (isObject(item) ? processObject(item) : item));
      }
      return current;
    }

    // Create a new object to avoid mutating the original
    let newObject: any = {};

    // Check if this object has Bindings at its root level
    if ("Bindings" in current && isObject(current.Bindings)) {
      // Process root-level bindings
      const { Bindings: rootBindings, ...objectWithoutBindings } = current;

      // Copy all non-binding properties first
      newObject = { ...objectWithoutBindings };

      // Process root-level bindings and apply them to the object
      for (const [bindingKey, bindingExpr] of Object.entries(rootBindings as Record<string, any>)) {
        const evaluated =
          bindingExpr === "Form" && !isNil(scope.Form)
            ? getFormData(scope.Form)
            : getExprValue(bindingExpr as string, scope, null) ?? "";

        if (evaluated === undefined) {
          delete newObject[bindingKey]; // remove literal if evaluation failed
        } else {
          newObject[bindingKey] = evaluated;
        }
      }
    } else {
      // No root-level bindings, copy all properties
      newObject = { ...current };
    }

    // Now process each property for nested objects and their bindings
    for (const [key, value] of Object.entries(newObject)) {
      if (isArray(value)) {
        // Process arrays recursively, creating a new array
        newObject[key] = value.map((item: any) => (isObject(item) ? processObject(item) : item));
      } else if (isObject(value) && !isNil(value)) {
        // Recursively process nested objects
        newObject[key] = processObject(value);
      }
      // Primitive values are already handled above
    }

    return newObject;
  }

  return processObject(obj);
}

const GET_OBJECT_DATA_DEBOUNCE_INTERVAL = 100; // milliseconds

type CacheEntry = {
  timestamp: number;
  result: any;
};

// WeakMap from object → Map from maxArraySize to CacheEntry
const cache = new WeakMap<object, Map<number | undefined, CacheEntry>>();

/**
 * Drop-in replacement for getObjectData(obj, maxArraySize?)
 * which uses a WeakMap to debounce repeated calls on the same object.
 */
export function getObjectDataDebounced(obj: any, maxArraySize?: number): any {
  if (obj === null || typeof obj !== "object") {
    // Primitives or null: just delegate immediately
    return getObjectData(obj, maxArraySize);
  }

  let innerMap = cache.get(obj);
  if (!innerMap) {
    innerMap = new Map();
    cache.set(obj, innerMap);
  }

  const now = Date.now();
  const entry = innerMap.get(maxArraySize);

  if (entry && now - entry.timestamp < GET_OBJECT_DATA_DEBOUNCE_INTERVAL) {
    log.debug("getObjectDataDebounced", "returning cached result");
    return entry.result;
  }

  const freshResult = getObjectData(obj, maxArraySize);
  innerMap.set(maxArraySize, { timestamp: now, result: freshResult });

  log.debug("getObjectDataDebounced", "returning fresh result");
  return freshResult;
}

export async function GetDynamicPage(pageSlug, context) {
  log.info(tag, "Service request", "project.page.get", pageSlug);
  const resp = await callServiceLight(
    { Name: "project.page.get", Service: "project.page.get" },
    { Id: pageSlug },
    context
  );

  if (!resp?.success) {
    return false;
  }
  return resp?.data.Entity;
}

// TODO: this function will be deprecated once all pages are stored server side
export async function GetStaticPage(pageSlug) {
  //const fileName = slugToCamelCase(pageSlug);
  const page = await import(`../sampleData/${pageSlug}/${pageSlug}.ts`);

  return page.default;
}
